import React, { useState } from 'react';

import { parseISO } from 'date-fns';

import { Box } from '@vk-hr-tek/ui/Box';
import { MenuItem } from '@vk-hr-tek/ui/MenuItem';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { DropdownButton } from '@vk-hr-tek/ui/DropdownButton';
import { useInject } from '@vk-hr-tek/core/ioc';
import { FormatService } from '@vk-hr-tek/core/format';
import { Button } from '@vk-hr-tek/ui/Button';
import { Icon } from '@vk-hr-tek/ui/Icon';

import {
  ContentSurveysFormValues,
  DateExplanationBadgeModule,
  ExternalDeps,
} from '../../types';

import { DateExplanationBadge } from './DateExplanationBadge';

interface ContentSurveyCardProps {
  bgWhite: boolean;
  onUpdate: VoidFunction;
  onDelete: VoidFunction;
  externalDeps: ExternalDeps;
  contentSurvey: ContentSurveysFormValues;
  module?: DateExplanationBadgeModule;
}

export const ContentSurveyCard = ({
  bgWhite,
  onUpdate,
  onDelete,
  externalDeps,
  contentSurvey,
  module,
}: ContentSurveyCardProps) => {
  const format = useInject(FormatService);

  const [hovered, setHovered] = useState<boolean>(false);

  const {
    settings: { completionDate, completionTime },
  } = contentSurvey;

  const getDateExplanationBadgeData = () => {
    if (!completionDate || !completionTime) {
      return { value: false };
    }

    const completionDateTime = new Date(`${completionDate}T${completionTime}`);

    if (isNaN(completionDateTime.getTime())) {
      return { value: false };
    }

    if (externalDeps.parentComplete) {
      const parentComplete = parseISO(externalDeps.parentComplete);

      return {
        value: completionDateTime < parentComplete,
        label: 'Дата в прошлом',
        tooltip:
          'Дата завершения опроса выбрана в прошлом. После публикации поста опрос будет завершен',
      };
    }

    return { value: new Date() > completionDateTime };
  };

  const handleMouseEnter = () => {
    setHovered(true);
  };

  const handleMouseLeave = () => {
    setHovered(false);
  };

  const dateExplanationBadgeData = getDateExplanationBadgeData();

  return (
    <Box
      py="8"
      px="12"
      radius="l"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      bgcolor={bgWhite ? 'bg.greyscale.primary' : 'bg.light.primary'}
    >
      <Box gap="2" display="flex" flexDirection="column" minWidth={0}>
        <Typography variant="subtitle2" noWrap>
          {contentSurvey.common.name}
        </Typography>

        <Box gap="8" display="flex" alignItems="center">
          <Typography variant="body3" color="text.light.tertirary">
            {completionDate && completionTime
              ? `Срок прохождения: до ${[format.toDate(completionDate), completionTime].join(' ')}`
              : 'Срок прохождения не ограничен'}
          </Typography>

          {dateExplanationBadgeData.value && (
            <DateExplanationBadge
              bgWhite={bgWhite}
              label={dateExplanationBadgeData?.label}
              tooltip={dateExplanationBadgeData?.tooltip}
              module={module}
            />
          )}
        </Box>
      </Box>

      <Box display={hovered ? 'block' : 'none'}>
        <DropdownButton
          variant="tertiaryLight"
          verticalPosition="bottom"
          horizontalPosition="right"
          button={({ onClick }) => (
            <Button
              size="small"
              disableRipple
              onClick={onClick}
              variant="quaternaryAccent"
              icon={
                <Icon
                  size="16"
                  name="more_vertical"
                  color="text.dark.tertirary"
                />
              }
            />
          )}
          menuItems={
            <Box display="flex" flexDirection="column">
              <MenuItem onClick={onUpdate}>
                <Typography variant="body3" color="text.light.primary">
                  Редактировать
                </Typography>
              </MenuItem>

              <MenuItem onClick={onDelete}>
                <Typography variant="body3" color="text.light.primary">
                  Удалить
                </Typography>
              </MenuItem>
            </Box>
          }
        />
      </Box>
    </Box>
  );
};
